import {
  ACL_AREA_VIEW_ORDERS, ACL_AREA_MANAGE_STORES, ACL_AREA_MANAGE_MIDS, ACL_AREA_VIEW_PRODUCTS,
  ACL_AREA_MANAGE_SUBSCRIPTIONS, ACL_AREA_MANAGE_RMA,
  ACL_AREA_PARTICIPATE_REFERRALS, ACL_AREA_MANAGE_MEMBERSHIP, ACL_AREA_READ_NOTIFICATIONS,
  ACL_AREA_MANAGE_ADDRESSES,
  type AclArea, type SelfViewWire,
} from "./membership-api";

// ⚠️ AMENDED 2026-10-02 (account-area redesign, Task 4). ORIGINAL: seven sections
// (welcome, profile, orders, products, merchant-service, refer, members), Profile second,
// "Welcome" and "Merchant Service" labels, Notifications folded into Welcome, and the
// customer's stores reached through a "Manage stores" link inside Products.
// CURRENT: nine sections in the approved IA order (D2) with the approved labels (D3) and the
// operator's wording ("Merchant services", "Stores" as its own section), plus a tab registry
// (SECTION_TABS) naming the sub-navigation each mixed-content section uses. Why: the operator
// asked for one kind of information per page and for tabs where a page mixes kinds.

export type SectionId =
  | "overview" | "orders" | "products" | "stores" | "merchant-service"
  | "refer" | "notifications" | "profile" | "members";

export interface AccountSection {
  id: SectionId;
  label: string;
  /** One sentence under the page title: what the page is for. */
  description: string;
  /**
   * ACL area gating the WHOLE section. Undefined = always visible.
   *
   * Profile is deliberately ungated: its sub-sections carry the real gates
   * inside the panels, and ungated Profile is what keeps "Leave this account"
   * reachable for every member (section 3.2, ruling R2). Overview is ungated
   * for the same landing-page reason.
   *
   * Typed to the `AclArea` union (TD-472), not `string` — a typo'd area key
   * is now a compile error instead of silently matching no key in
   * `effective_permissions` and defaulting to visible.
   */
  area?: AclArea;
}

/**
 * The account sections, in the approved order (D2) with the approved labels (D3).
 *
 * Action Items and Billing are deliberately ABSENT rather than present and
 * disabled: a section exists when its unit ships it. account-center parity
 * tests assert that every entry here renders something and that nothing
 * renders which is not here.
 *
 * ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-583). ORIGINAL: "`products` keeps the
 * manage-stores gate it had (ruling R3: presentation only; the mismatch with the data's
 * real areas is recorded as tech debt and the server's refusal stays the control)."
 * CURRENT: `products` is gated by its own area, view-products (migration 150). Operator
 * answer 2026-10-10: "New products permission" -- "A dedicated ACL area. Needs a seed
 * migration and a column in the portal permission matrix." Stores keeps manage-stores.
 * The data inside Products is still governed by view-orders, manage-subscriptions,
 * manage-stores and manage-rma; `productsAccess` lets each panel degrade on its own area
 * so the new area can neither open a hole nor produce a dead page.
 */
export const SECTIONS: readonly AccountSection[] = [
  { id: "overview", label: "Overview", description: "Your account at a glance." },
  { id: "orders", label: "Orders", description: "Track and review the orders placed on this account.", area: ACL_AREA_VIEW_ORDERS },
  { id: "products", label: "Products & subscriptions", description: "What you own, what renews, and where it is used.", area: ACL_AREA_VIEW_PRODUCTS },
  { id: "stores", label: "Stores", description: "The locations you buy for.", area: ACL_AREA_MANAGE_STORES },
  { id: "merchant-service", label: "Merchant services", description: "The merchant accounts linked to your stores.", area: ACL_AREA_MANAGE_MIDS },
  { id: "refer", label: "Refer & Save", description: "Share what works for you and see what it earns.", area: ACL_AREA_PARTICIPATE_REFERRALS },
  { id: "notifications", label: "Notifications", description: "Updates about your account.", area: ACL_AREA_READ_NOTIFICATIONS },
  { id: "profile", label: "Profile & security", description: "Your details, addresses and how you sign in." },
  { id: "members", label: "Members", description: "The people who can act on this account.", area: ACL_AREA_MANAGE_MEMBERSHIP },
];

export const DEFAULT_SECTION: SectionId = "overview";

export function sectionLabel(id: SectionId): string {
  return SECTIONS.find((s) => s.id === id)?.label ?? id;
}

export interface SectionTab {
  id: string;
  label: string;
  /** ACL area gating this tab alone. Undefined = visible whenever the section is. */
  area?: AclArea;
  /** Extra visibility rule beyond `area` (for example owner-only). */
  visibleWhen?: (sv: SelfViewWire | null) => boolean;
}

/**
 * Sub-navigation for the sections that mix kinds of information (F16, F21, F22). Panels render
 * these with EmbedTabs; selection is React state only (rule 7: never storage). Sections with
 * no entry here have one kind of information and no tabs. The Profile tab is ungated on
 * purpose: "Leave this account" is the last block of that tab and must stay reachable for
 * every member.
 */
export const SECTION_TABS: Partial<Record<SectionId, readonly SectionTab[]>> = {
  products: [
    { id: "subscriptions", label: "Subscriptions" },
    { id: "hardware", label: "Hardware & one-time" },
  ],
  refer: [
    { id: "welcome", label: "Welcome" },
    { id: "refer", label: "Refer a store" },
    { id: "referrals", label: "Referrals" },
    { id: "redemptions", label: "Redemptions" },
  ],
  profile: [
    { id: "profile", label: "Profile" },
    { id: "addresses", label: "Addresses", area: ACL_AREA_MANAGE_ADDRESSES },
    { id: "security", label: "Sign-in & security" },
  ],
  members: [
    { id: "members", label: "Members" },
    { id: "invitations", label: "Invitations" },
    { id: "ownership", label: "Ownership", visibleWhen: (sv) => sv?.role === "owner" },
  ],
};

/**
 * Presentation gating, unchanged from the pre-Unit-1 rule: an area is
 * permitted unless a LOADED self-view map says explicitly false. An unloaded
 * self-view hides nothing - the server's own area refusals remain the actual
 * control, so the worst case is a visible surface whose use 403s.
 */
export function isAreaPermitted(selfView: SelfViewWire | null, area?: AclArea): boolean {
  if (!area) return true;
  return selfView?.effective_permissions?.[area] !== false;
}

export function visibleSections(selfView: SelfViewWire | null): AccountSection[] {
  return SECTIONS.filter((s) => isAreaPermitted(selfView, s.area));
}

export function visibleTabs(id: SectionId, selfView: SelfViewWire | null): SectionTab[] {
  return (SECTION_TABS[id] ?? []).filter(
    (tab) => isAreaPermitted(selfView, tab.area) && (tab.visibleWhen ? tab.visibleWhen(selfView) : true),
  );
}

/**
 * Which of the Products section's DATA areas the member holds. The section's own gate
 * (view-products) only decides whether it is offered; every panel inside reads a different
 * area, so each one degrades on ITS OWN (TD-583). Same presentation semantics as
 * `isAreaPermitted`: an unloaded self-view permits everything and the server's refusals
 * stay the control.
 *   - orders:        view-orders          -- the owned-products list is DERIVED from orders
 *   - subscriptions: manage-subscriptions -- the Subscriptions tab, cancel and resume
 *   - stores:        manage-stores        -- the "Move units" action (reads /me/stores, posts a move)
 *   - returns:       manage-rma           -- "Request a return" and the return Status
 */
export interface ProductsAccess {
  orders: boolean;
  subscriptions: boolean;
  stores: boolean;
  returns: boolean;
}

export const FULL_PRODUCTS_ACCESS: ProductsAccess = { orders: true, subscriptions: true, stores: true, returns: true };

export function productsAccess(selfView: SelfViewWire | null): ProductsAccess {
  return {
    orders: isAreaPermitted(selfView, ACL_AREA_VIEW_ORDERS),
    subscriptions: isAreaPermitted(selfView, ACL_AREA_MANAGE_SUBSCRIPTIONS),
    stores: isAreaPermitted(selfView, ACL_AREA_MANAGE_STORES),
    returns: isAreaPermitted(selfView, ACL_AREA_MANAGE_RMA),
  };
}
